import React, { useState } from "react";
import { z } from "zod";

import type { ExtendedWorkspace } from "@performyard/service-contracts";

import { SingleEmployeeSchema } from "../../../../components/EmployeePicker/utils";
import WorkspaceFormModal from "./WorkspaceFormModal";

import { useCurrentUser, useUpdateMeetingWorkspace } from "../../../../api/hooks";
import useForm from "../../../../hooks/useForm";

type UpdateWorkspaceFormProps = {
	isOpen: boolean;
	handleClose: () => void;
	workspace: ExtendedWorkspace | null;
};

const UpdateWorkspaceForm = ({ isOpen, handleClose, workspace }: UpdateWorkspaceFormProps) => {
	// Current user will always be a participant
	const { data: currentUser } = useCurrentUser();
	const [error, setError] = useState<Record<string, { message: string }>>({});

	// Selected participant is the participant that is not the current user
	const selectedParticipant = workspace?.participants.find(
		(participant) => participant.userId !== currentUser?.me?.authorization?.payload.user_id
	);

	// Workspace form state
	const { validate, resetForm, updateForm, state } = useForm({
		schema: z.object({
			name: z
				.string()
				.min(1, { message: "This is a required field" })
				.max(280, { message: "Name cannot exceed 280 characters" }),
			participant: SingleEmployeeSchema.nullable(),
		}),
		defaultValues: {
			name: workspace?.name ?? "",
			participant: {
				value: selectedParticipant?.userId ?? "",
				label: selectedParticipant?.name ?? "",
			},
		},
	});

	// Update workspace request
	const { mutate: updateWorkspace, isPending } = useUpdateMeetingWorkspace({
		onSuccess: () => {
			onClose();
		},
	});

	const onSubmit = () => {
		if (!currentUser?.me?.authorization?.payload.user_id || !workspace) {
			return;
		}

		// Validate the form
		const validationResult = validate();
		if (!validationResult.isValid) {
			setError(validationResult.errors);
			return;
		}

		// Update the workspace
		updateWorkspace({
			workspaceId: workspace._id,
			data: {
				// Only updating name
				name: state.name,
			},
		});
	};

	// Reset form and close modal
	const onClose = () => {
		resetForm();
		setError({});
		handleClose();
	};

	return (
		<WorkspaceFormModal
			handleSubmit={onSubmit}
			handleClose={onClose}
			strings={{
				title: "Update Meeting Workspace",
				cancel: "Cancel",
				submit: "Save",
			}}
			state={{
				...state,
				isOpen,
				isPending,
				isParticipantEditable: false,
				error,
			}}
			updateForm={updateForm}
		/>
	);
};

export default UpdateWorkspaceForm;
